/**
 * Revenue. Is the portfolio pacing, and against what? Phase 4, plan 21.
 *
 * ---------------------------------------------------------------------------------------------
 * THE ONE RULE THIS SCREEN IS BUILT AROUND
 * ---------------------------------------------------------------------------------------------
 *
 * **No number here is computed here.** Occupancy, ADR, RevPAR, the year-over-year change and the
 * pace all arrive from `/api/revenue/*`, which gets them from `packages/db` over
 * `packages/finance` — the same functions the Revenue Agent's data scopes call. The browser
 * formats and sorts; it does not divide (CLAUDE.md §3.6).
 *
 * The delta column is the closest this file comes to arithmetic, and it is deliberately a
 * SUBTRACTION OF TWO FRACTIONS THE API ALREADY COMPUTED rather than a ratio of its own. If it
 * needed a division it would belong in the engine, where it could be tested.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY EVERY FIGURE HAS A SECOND FIGURE BESIDE IT
 * ---------------------------------------------------------------------------------------------
 *
 * "Occupancy 45%" is not information. It is either a house with a problem or a January, and only
 * the comparison separates them — which is exactly the judgement the Revenue Agent is evaluated
 * on (EVALUATIONS.md §6.3). So the portfolio is shown for both periods, every property carries its
 * own last year, and the table sorts by the change rather than by the level, because the houses
 * that MOVED are the ones worth a person's attention.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type PortfolioResponse,
  type PropertyPerformance,
  type RevenuePropertiesResponse,
} from '../api.js';
import { formatMoney } from '../money.js';

type SortKey = 'change' | 'occupancy' | 'revpar' | 'name';

/** A fraction as a percentage with one decimal. Presentation only; the fraction is the source. */
function percent(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

/**
 * The change between two occupancies, in percentage POINTS.
 *
 * Points, not percent: 45% against 50% is five points down, and calling it "10% down" is a
 * different statement about a different quantity. DATA_MODEL.md §7 says the sign convention is
 * documented per line rather than assumed, so it is documented here — positive is better than
 * last year.
 */
function pointsChange(now: number | null, before: number | null): number | null {
  if (now === null || before === null) return null;
  return (now - before) * 100;
}

function Delta({ points }: { points: number | null }): ReactNode {
  if (points === null) {
    return (
      <span className="muted" title="No figure for the same window last year to compare against">
        no comparison
      </span>
    );
  }
  const rounded = Math.round(points * 10) / 10;
  const tone = rounded > 0 ? 'chip chip-ok' : rounded < 0 ? 'chip chip-attention' : 'chip';
  return (
    <span className={tone}>
      {rounded > 0 ? '+' : ''}
      {rounded.toFixed(1)} pts
    </span>
  );
}

function money(value: string | null): string {
  return value === null ? '—' : formatMoney(value);
}

export function RevenuePage(): ReactNode {
  const [sort, setSort] = useState<SortKey>('change');

  const portfolio = useQuery({
    queryKey: ['revenue', 'portfolio'],
    queryFn: () => api.get<PortfolioResponse>('/api/revenue/portfolio'),
  });
  const properties = useQuery({
    queryKey: ['revenue', 'properties'],
    queryFn: () => api.get<RevenuePropertiesResponse>('/api/revenue/properties'),
  });

  const rows = useMemo(() => {
    const list = [...(properties.data?.properties ?? [])];
    const change = (p: PropertyPerformance): number =>
      pointsChange(p.trailing.occupancy, p.trailingLastYear.occupancy) ?? Number.POSITIVE_INFINITY;

    // Ascending by change, so the property that fell furthest is first. A property with no
    // comparison sorts last rather than first: "no history" is not "worst", and putting it at the
    // top would make the screen open on the least actionable row it has.
    if (sort === 'change') list.sort((a, b) => change(a) - change(b));
    if (sort === 'occupancy')
      list.sort((a, b) => (a.trailing.occupancy ?? 1) - (b.trailing.occupancy ?? 1));
    if (sort === 'revpar') {
      list.sort((a, b) => Number(a.trailing.revpar ?? 0) - Number(b.trailing.revpar ?? 0));
    }
    if (sort === 'name') list.sort((a, b) => a.propertyName.localeCompare(b.propertyName));
    return list;
  }, [properties.data, sort]);

  const windows = portfolio.data?.windows ?? properties.data?.windows ?? null;
  const p = portfolio.data;

  return (
    <div className="page">
      <header className="page-header">
        <h1>Revenue</h1>
        <p className="muted">
          {windows ? (
            <>
              The last three completed months, {windows.trailing.from} to {windows.trailing.to},
              against the same three of the year before. Every figure is computed by the finance
              engine — the same one the Revenue Agent reads.
            </>
          ) : (
            'Loading…'
          )}
        </p>
      </header>

      {portfolio.isError && <div className="alert">{portfolio.error.message}</div>}
      {properties.isError && <div className="alert">{properties.error.message}</div>}

      {p && (
        <section className="card">
          <h2>The portfolio</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Period</th>
                <th className="num">Properties</th>
                <th className="num">Booked</th>
                <th className="num">Sellable</th>
                <th className="num">Blocked</th>
                <th className="num">Occupancy</th>
                <th className="num">ADR</th>
                <th className="num">RevPAR</th>
                <th className="num">Room revenue</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ['This period', p.trailing],
                  ['A year earlier', p.trailingLastYear],
                ] as const
              ).map(([label, period]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td className="num">{period.properties}</td>
                  <td className="num">{period.bookedNights}</td>
                  {/* Blocked nights are shown apart from sellable ones, because they are NOT in
                      occupancy's denominator: a night nobody could sell is not a night nobody
                      bought, and folding them together would report a problem that is a
                      renovation. */}
                  <td className="num">{period.sellableNights}</td>
                  <td className="num">{period.blockedNights}</td>
                  <td className="num">{percent(period.occupancy)}</td>
                  <td className="num">{money(period.adr)}</td>
                  <td className="num">{money(period.revpar)}</td>
                  <td className="num">{money(period.roomRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small" title="derived.property_month, then packages/finance">
            {p.trailing.calcMethod}
          </p>
        </section>
      )}

      {p && p.channelMix.length > 0 && (
        <section className="card">
          <h2>Channel mix</h2>
          <p className="muted">
            By bookings and by revenue, because they disagree — and the disagreement is the finding.
            A channel supplying a third of the bookings and a fifth of the revenue is saying
            something about its guests or its commission that a count alone would hide.
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Channel</th>
                <th className="num">Bookings</th>
                <th className="num">Share of bookings</th>
                <th className="num">Room revenue</th>
                <th className="num">Share of revenue</th>
              </tr>
            </thead>
            <tbody>
              {p.channelMix.map((c) => (
                <tr key={c.channel}>
                  <td>{c.channel}</td>
                  <td className="num">{c.bookings}</td>
                  <td className="num">{percent(c.bookingShare)}</td>
                  <td className="num">{money(c.roomRevenue)}</td>
                  <td className="num">{percent(c.revenueShare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="card">
        <div className="table-header">
          <h2>By property</h2>
          <label>
            Sort by
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="change">Change against last year</option>
              <option value="occupancy">Occupancy</option>
              <option value="revpar">RevPAR</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>

        {properties.isLoading && <p className="muted">Loading…</p>}
        {!properties.isLoading && rows.length === 0 && (
          <p className="muted">
            No property has any nights in either window. That is not an error — it is what an empty
            book looks like.
          </p>
        )}

        {rows.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Property</th>
                <th className="num">Occupancy</th>
                <th className="num">Last year</th>
                <th>Change</th>
                <th className="num">ADR</th>
                <th className="num">RevPAR</th>
                <th className="num">Room revenue</th>
                <th className="num">Forward nights</th>
                <th className="num">Last year at the same point</th>
                <th>Pace</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.propertyId}>
                  <td>{row.propertyName}</td>
                  <td className="num">{percent(row.trailing.occupancy)}</td>
                  <td className="num">{percent(row.trailingLastYear.occupancy)}</td>
                  <td>
                    <Delta
                      points={pointsChange(row.trailing.occupancy, row.trailingLastYear.occupancy)}
                    />
                  </td>
                  <td className="num">{money(row.trailing.adr)}</td>
                  <td className="num">{money(row.trailing.revpar)}</td>
                  <td className="num">{money(row.trailing.roomRevenue)}</td>
                  <td className="num">{row.forward.nights}</td>
                  <td className="num">{row.forwardLastYear.nights}</td>
                  <td>
                    {row.pace.nightsRatio === null ? (
                      <span className="muted" title="Nothing on the books a year ago to compare">
                        no comparison
                      </span>
                    ) : (
                      <span
                        className={
                          row.pace.nightsRatio >= 1 ? 'chip chip-ok' : 'chip chip-attention'
                        }
                        title="This year's forward book divided by last year's, both read at the same distance from their window"
                      >
                        {row.pace.nightsRatio.toFixed(2)}×
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
